import { STAFF_APP_ROLES, type AppRole } from "@/hooks/useUserRoles";
import { FEATURE_FLAGS } from "@/lib/feature-flags";

export type OrgRole = "owner" | "admin" | "member" | null | undefined;

export type MfaPolicy = "optional" | "required_roles" | "required_staff";

export interface MfaEnforcementSettings {
  mfaPolicy?: MfaPolicy;
  mfaRequiredRoles?: AppRole[];
}

export function isMfaDeferred(
  _userMetadata?: Record<string, unknown>,
): boolean {
  // Do not trust client-controlled state for MFA authorization decisions.
  // `user_metadata` and localStorage can be modified by the authenticated user.
  return false;
}

const MFA_REQUIRED_PLATFORM_ROLES: AppRole[] = [
  "super_admin",
  "platform_support",
  "platform_super_admin",
];

const STAFF_ROLE_SET = new Set<AppRole>(STAFF_APP_ROLES);

export function requiresMfaForRoles(
  roles: AppRole[],
  organizationRole: OrgRole,
  settings?: MfaEnforcementSettings,
  userMetadata?: Record<string, unknown>,
  _userId?: string,
) {
  if (!FEATURE_FLAGS.mfa) {
    return false;
  }

  if (roles.some((role) => MFA_REQUIRED_PLATFORM_ROLES.includes(role))) {
    return true;
  }

  // Check if MFA is deferred for non-platform roles (platform roles return above)
  if (isMfaDeferred(userMetadata)) {
    return false;
  }

  const policy = settings?.mfaPolicy ?? "optional";
  const requiredRoles = settings?.mfaRequiredRoles ?? [];
  const isOrgAdmin = organizationRole === "owner" || organizationRole === "admin";
  const isStaff = roles.some((role) => STAFF_ROLE_SET.has(role)) || isOrgAdmin;

  if (policy === "required_staff") {
    return isStaff;
  }

  if (policy === "required_roles") {
    const hasRequiredRole = roles.some((role) => requiredRoles.includes(role));
    const orgAdminRequiresMfa = isOrgAdmin && requiredRoles.includes("admin");
    return hasRequiredRole || orgAdminRequiresMfa;
  }

  return false;
}
